import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type Project, type ProjectPatch } from './api';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const SAVE_DEBOUNCE_MS = 600;

/**
 * Loads a project and keeps it saved as the customer works.
 *
 * Edits land in local state immediately so typing never stutters, and are
 * flushed to the server on a debounce. Pending answer keys accumulate between
 * flushes, so a burst of edits across several fields becomes one PATCH rather
 * than one per keystroke. `flush` forces the pending write out and resolves
 * once it lands, which is what the Continue button awaits before navigating.
 */
export function useProject(id: string | undefined) {
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  const pending = useRef<ProjectPatch>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    api
      .getProject(id)
      .then((p) => {
        if (!cancelled) setProject(p);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(
          e instanceof ApiError && e.status === 404
            ? 'We could not find that project. It may have been removed.'
            : 'We could not load your project. Try again in a moment.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const push = useCallback(async () => {
    if (!id) return;
    const patch = pending.current;
    pending.current = {};
    if (Object.keys(patch).length === 0) return;

    setSaveState('saving');
    try {
      const updated = await api.updateProject(id, patch);
      setProject(updated);
      setSaveState('saved');
    } catch {
      // Put the work back so the next flush retries it rather than losing it.
      pending.current = { ...patch, ...pending.current };
      setSaveState('error');
    }
  }, [id]);

  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      inFlight.current = push();
    }, SAVE_DEBOUNCE_MS);
  }, [push]);

  /** Force any pending write out now. Resolves when it has landed. */
  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    await inFlight.current;
    await push();
  }, [push]);

  const setAnswer = useCallback(
    (key: string, value: unknown) => {
      setProject((p) => (p ? { ...p, answers: { ...p.answers, [key]: value } } : p));
      pending.current.answers = { ...pending.current.answers, [key]: value };
      schedule();
    },
    [schedule],
  );

  /** Update a top-level project field (size, system, status). */
  const setField = useCallback(
    <K extends keyof ProjectPatch>(key: K, value: ProjectPatch[K]) => {
      setProject((p) => (p ? { ...p, [key]: value } : p));
      pending.current[key] = value;
      schedule();
    },
    [schedule],
  );

  // Flush on unmount so navigating away never drops the last edit.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      void push();
    },
    [push],
  );

  return { project, loading, loadError, saveState, setAnswer, setField, flush };
}
